/* eslint-disable */
import { Badge, Button, Paper } from "@mui/material";
import {
  DateCalendar,
  DayCalendarSkeleton,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useReducer, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import RHFSelect from "src/components/elements/Select/RHFSelect";
import { useRoleUser } from "src/hooks/query/usersByRole";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
import * as yup from "yup";
import {
  FetchMonthSlotsRequest,
  HighlightedDay,
  HighlightedDaysRequest,
  MonthSlotProps,
  RescheduleInterviewRequest,
  SlotsProps,
} from "../../types";
import { RiDeleteBinLine } from "react-icons/ri";
import moment from "moment";
import RHFAutoComplete from "src/components/elements/AutoComplete/RHFAutoComplete";
import API from "../../../../network";
import { confirmWrapper } from "src/components/Dialog";
import { DialogContentProps } from "src/components/Dialog/Dialog";

export interface RescheduleInterviewProps extends DialogContentProps {
  id: number;
  rescheduleInterviewSlot:
  (data: RescheduleInterviewRequest, close: (() => void) | undefined) => Promise<void>;
}

export interface RescheduleFormProps {
  interviewer: number;
}

const initialState = {
  daySlots: [] as SlotsProps[],
  date: new Date().valueOf() as number | undefined,
  selectedSlot: undefined as number | undefined,
  monthSlots: {} as MonthSlotProps,
  highlightedDays: [] as Array<HighlightedDay>,
};

type Action =
  | { type: "SET_DAY_SLOTS"; payload: SlotsProps[] | [] }
  | { type: "SET_DATE"; payload: number | undefined }
  | { type: "SET_SELECTED_SLOT"; payload: number | undefined }
  | { type: "SET_MONTH_SLOTS"; payload: MonthSlotProps }
  | { type: "SET_HIGHLIGHTED_DAYS"; payload: Array<HighlightedDay> };

const validationSchema = yup.object().shape({
  interviewer: yup
    .number()
    .typeError("Select time zone")
    .required("Time zone required"),
});

function RescheduleInterviewPopup(props: RescheduleInterviewProps) {
  const { id, rescheduleInterviewSlot, close} = props;

  const [state, dispatch] = useReducer(
    (state: typeof initialState, action: Action) => {
      switch (action.type) {
        case "SET_DAY_SLOTS":
          return { ...state, daySlots: action.payload };
        case "SET_DATE":
          return { ...state, date: action.payload };
        case "SET_SELECTED_SLOT":
          return { ...state, selectedSlot: action.payload };
        case "SET_MONTH_SLOTS":
          return { ...state, monthSlots: action.payload };
        case "SET_HIGHLIGHTED_DAYS":
          return { ...state, highlightedDays: action.payload };
        default:
          return state;
      }
    },
    initialState
  );

  const getSlotsByMonth = async (data: HighlightedDaysRequest) => {
    const { month, year, interviewerId } = data;
    try {
      const finalData: FetchMonthSlotsRequest = {
        month: month + 1,
        year,
        statuses: ["OPEN"],
        interviewerId,
      };
      const fetchedSlots = await API.getInstance().getMonthSlots(finalData);

      // Extract the keys (dates) and create the highlightedDays array
      const highlightedDays = fetchedSlots?.OPEN
        ? Object.keys(fetchedSlots?.OPEN).map((date) => ({
            date,
            slots: fetchedSlots?.OPEN[date].length,
          }))
        : [];

        dispatch({
          type: 'SET_MONTH_SLOTS',
          payload: fetchedSlots.OPEN,
        })
        dispatch({
          type: 'SET_HIGHLIGHTED_DAYS',
          payload: highlightedDays,
        })
        dispatch({ type: "SET_SELECTED_SLOT", payload: undefined });
    } catch (err) {
      await confirmWrapper("Something went wrong", {
        hideSecondaryBtn: true,
        variant: "danger",
      });
    }
  };

  function ServerDay(props: PickersDayProps<Dayjs>) {
    const { day, outsideCurrentMonth, ...other } = props;

    const dayString = day.format("MM/DD/YYYY"); // Format the day to match the format of highlightedDays

    const highlightedDay = state.highlightedDays.find(
      (highlight) => highlight.date === dayString
    );

    const isSelected = !outsideCurrentMonth && highlightedDay !== undefined;

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        badgeContent={
          isSelected ? (
            <div className="w-[10px] h-[10px] flex justify-center rounded-xl bg-lightGray border-[1px] border-solid border-primary absolute top-1 left-0 text-primary font-extrabold text-[8px]">
              {highlightedDay?.slots}
            </div>
          ) : undefined
        }
        // badgeContent={isSelected ? '📌' : undefined}
        // badgeContent={isSelected ? '🟢' : undefined}
        // badgeContent={isSelected ? '🌚' : undefined}
      >
        <PickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
        />
      </Badge>
    );
  }

  const resolver = useYupValidationResolver(validationSchema);

  const methods = useForm<RescheduleFormProps>({
    resolver,
  });

  const { handleSubmit, watch } = methods;

  const { userByRoleOptionsWithEmail, usersLoading, fetchByRole } = useRoleUser({
    roleNameList: "ATSInterviewer",
  });

  const handleDateChange = (date: number | undefined) => {
    if (date !== undefined) {
      const formattedDate = moment(date).format("MM/DD/YYYY");
      const daySlots = state.monthSlots?.[formattedDate];
      dispatch({ type: "SET_DAY_SLOTS", payload: daySlots });
      dispatch({ type: "SET_SELECTED_SLOT", payload: undefined });
    }
  };

  const handleMonthOrYearChange = (date: Dayjs) => {
    dispatch({
      type: "SET_DATE",
      payload: date.valueOf(),
    });
    dispatch({ type: "SET_DAY_SLOTS", payload: [] });
    const interviewerId = watch("interviewer");
    const data: HighlightedDaysRequest = {
      month: date.month(),
      year: date.year(),
      interviewerId,
    };
    getSlotsByMonth(data);
  };

  const updateInterviewSlot = () => {
    if (state.selectedSlot) {
      const data = {
        oldSlotId: id,
        newSlotId: state.selectedSlot,
      };
      rescheduleInterviewSlot(data, close);
    }
  };

  const handleChangeInterviewers = () => {
    handleMonthOrYearChange(dayjs(state.date));
  }

  useEffect(() => {
    handleChangeInterviewers();
  }, [watch("interviewer")]);

  const { highlightedDays } = state;

  return (
    <FormProvider {...methods}>
      <form>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Paper className="px-4 rounded-md text-left" elevation={0}>
            <div className="pb-16 text-secondary text-center text-lg font-bold">
              Reschedule Interview Slot
            </div>

            <div className="pb-16 flex gap-10">
              <DateCalendar
                defaultValue={dayjs(new Date())}
                minDate={dayjs(new Date())}
                // loading={isCalendarLoading}
                onMonthChange={handleMonthOrYearChange}
                onYearChange={handleMonthOrYearChange}
                renderLoading={() => <DayCalendarSkeleton />}
                onChange={(e) => {
                  const dateValue = e?.valueOf(); 
                  dispatch({
                    type: "SET_DATE",
                    payload: dateValue as number | undefined,
                  });
                  handleDateChange(dateValue);
                }}
                slots={{
                  day: ServerDay,
                }}
                slotProps={{
                  day: {
                    highlightedDays,
                  } as any,
                }}
                className="shadow-md bg-white w-1/2"
              />
              <div className="w-1/2 relative">
                <div className="font-bold text-md text-[#28364C] tracking-wide mb-1">
                  Select interviewer
                </div>
                <RHFAutoComplete
                  name="interviewer"
                  control={methods.control}
                  options={userByRoleOptionsWithEmail}
                  loading={usersLoading}
                />
                <div>
                  <div className="pt-5 pb-3 font-bold text-base text-[#28364c] tracking-wide">
                    Available slots
                  </div>
                  <div className="flex flex-col gap-2 max-h-[130px] overflow-auto">
                    {state.daySlots?.map((slot, index) => {
                      return (
                        <Button
                          key={index}
                          className="w-[300px] group"
                          onClick={() => {
                            dispatch({
                              type: "SET_SELECTED_SLOT",
                              payload: slot.id,
                            });
                          }}
                          variant={
                            slot.id === state.selectedSlot
                              ? "contained"
                              : "outlined"
                          }
                        >
                          <div className="flex justify-between items-center w-[80%]">
                            <div>{slot.startTime}</div>
                            <div>-</div>
                            <div>{slot.endTime}</div>
                            <div>{slot.timeZone}</div>
                          </div>
                        </Button>
                      );
                    })}
                  </div>
                </div>
                <Button
                  disabled={!state.selectedSlot}
                  variant="contained"
                  className="absolute bottom-0"
                  onClick={updateInterviewSlot}
                >
                  Update
                </Button>
              </div>
            </div>
            <div />
          </Paper>
        </LocalizationProvider>
      </form>
    </FormProvider>
  );
}

export default RescheduleInterviewPopup;
